import React, { useState, useCallback, useRef } from "react";
import {
  Box
} from '@chakra-ui/react';
import { MdCheck, MdClose } from 'react-icons/md';
import { DetailedScrapeJobGrid, BulkSelectionBar, BulkEditButton } from "components";
import { useDetailedStatusScrapejobs, useApproveScrapejob, useRejectScrapejob, useSanitizeScrapejob, useBulkApproveScrapejobs, useBulkRejectScrapejobs } from "hooks/queries/useScrapejobs";
import { useArtists } from "hooks/queries/useArtists";
import { useScrapesites } from "hooks/queries/useScrapesites";
import { ProcessStatus } from "models/types";
import { ScrapejobModel } from "models/scrapejob/ScrapejobModel";

export function ScrapejobOverview() {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(ProcessStatus.PENDING);
  const [page, setPage] = useState(1);
  const { data: scrapejobsPagination, isLoading } = useDetailedStatusScrapejobs(selectedStatus, page);
  const { data: artistsData } = useArtists();
  const { data: scrapesitesData } = useScrapesites();
  const approveScrapejobMutation = useApproveScrapejob();
  const rejectScrapejobMutation = useRejectScrapejob();
  const sanitizeScrapejobMutation = useSanitizeScrapejob();
  const bulkApproveMutation = useBulkApproveScrapejobs();
  const bulkRejectMutation = useBulkRejectScrapejobs();

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedScrapejobs, setSelectedScrapejobs] = useState<ScrapejobModel[]>([]);
  const visibleScrapejobsRef = useRef<ScrapejobModel[]>([]);

  // Get scrapejobs from pagination data
  const scrapejobList = scrapejobsPagination?.data || [];
  const currentPage = scrapejobsPagination?.currentPage || 1;
  const totalPages = scrapejobsPagination?.totalPages || 1;
  const hasPendingScrapejobs = scrapejobList.some(
    s => s.status.toLowerCase() === 'pending'
  );
  const artistsList = artistsData || [];
  const scrapesitesList = scrapesitesData || [];

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    setPage(1); // Reset to first page when status changes
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleApprove = useCallback((scrapejobId: string) => {
    approveScrapejobMutation.mutate({ scrapejob_id: scrapejobId });
  }, [approveScrapejobMutation]);

  const handleReject = useCallback((scrapejobId: string) => {
    rejectScrapejobMutation.mutate({ scrapejob_id: scrapejobId });
  }, [rejectScrapejobMutation]);

  const handleSanitize = useCallback((scrapejobId: string) => {
    sanitizeScrapejobMutation.mutate({ scrapejob_id: scrapejobId });
  }, [sanitizeScrapejobMutation]);

  // Selection mode handlers
  const handleEnterSelectionMode = useCallback(() => {
    setSelectionMode(true);
    setSelectedScrapejobs([]);
  }, []);

  const handleExitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedScrapejobs([]);
  }, []);

  const handleScrapejobSelect = useCallback((scrapejob: ScrapejobModel) => {
    setSelectedScrapejobs(prev => {
      const exists = prev.some(s => s.id === scrapejob.id);
      if (exists) {
        return prev.filter(s => s.id !== scrapejob.id);
      }
      return [...prev, scrapejob];
    });
  }, []);

  const handleFilteredScrapejobsChange = useCallback((scrapejobs: ScrapejobModel[]) => {
    visibleScrapejobsRef.current = scrapejobs;
  }, []);

  const handleSelectAll = useCallback(() => {
    // Only select pending scrapejobs from visible items
    const pendingScrapejobs = visibleScrapejobsRef.current.filter(
      s => s.status.toLowerCase() === 'pending'
    );
    setSelectedScrapejobs(pendingScrapejobs);
  }, []);

  const handleBulkApprove = useCallback(async () => {
    if (selectedScrapejobs.length === 0) return;

    const scrapejobIds = selectedScrapejobs.map(s => s.id);
    await bulkApproveMutation.mutateAsync(scrapejobIds);
    handleExitSelectionMode();
  }, [selectedScrapejobs, bulkApproveMutation, handleExitSelectionMode]);

  const handleBulkReject = useCallback(async () => {
    if (selectedScrapejobs.length === 0) return;

    const scrapejobIds = selectedScrapejobs.map(s => s.id);
    await bulkRejectMutation.mutateAsync(scrapejobIds);
    handleExitSelectionMode();
  }, [selectedScrapejobs, bulkRejectMutation, handleExitSelectionMode]);

  // Filter to only count pending scrapejobs for selection
  const pendingVisibleCount = visibleScrapejobsRef.current.filter(
    s => s.status.toLowerCase() === 'pending'
  ).length;

  return (
    <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh" mx="auto">
      <DetailedScrapeJobGrid
        scrapejobs={scrapejobList}
        isLoading={isLoading}
        onApprove={handleApprove}
        onReject={handleReject}
        onSanitize={handleSanitize}
        approveLoading={approveScrapejobMutation.isPending}
        rejectLoading={rejectScrapejobMutation.isPending}
        sanitizeLoading={sanitizeScrapejobMutation.isPending}
        defaultStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        availableArtists={artistsList}
        availableScrapesites={scrapesitesList}
        selectionMode={selectionMode}
        selectedScrapejobs={selectedScrapejobs}
        onScrapejobSelect={handleScrapejobSelect}
        onFilteredScrapejobsChange={handleFilteredScrapejobsChange}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      <BulkEditButton
        onClick={handleEnterSelectionMode}
        isVisible={!selectionMode && hasPendingScrapejobs}
      />

      {/* Selection Bar - shown when in selection mode */}
      {selectionMode && (
        <BulkSelectionBar
          selectedCount={selectedScrapejobs.length}
          totalCount={pendingVisibleCount}
          onSelectAll={handleSelectAll}
          actions={[
            {
              label: 'Approve',
              colorScheme: 'green',
              icon: MdCheck,
              onClick: handleBulkApprove
            },
            {
              label: 'Reject',
              colorScheme: 'red',
              icon: MdClose,
              onClick: handleBulkReject
            }
          ]}
          onCancel={handleExitSelectionMode}
          isLoading={bulkApproveMutation.isPending || bulkRejectMutation.isPending}
          entityName="scrapejob"
        />
      )}
    </Box>
  );
}

export default ScrapejobOverview;
